/**
 * Family Hub Quick Setup Wizard, the read-only Example mode, and the empty-hub nudge.
 *
 * Kept out of workspace.js on purpose: that file owns the live hub (entries, sharing, SignalR), and this
 * owns two things that deliberately do NOT touch live state until the very end — a six-step form that
 * posts once, and an example view that renders sample content and puts the real DOM back when it exits.
 *
 * Depends on window.__workspaceData, the same handshake workspace.js uses.
 */
import { csrfHeaders } from './csrf';
import { toast } from './toast';

const TOTAL_STEPS = 6;

/** Mirrors FamilyChildLimits.MaxChildren; the server truncates past this either way. */
const MAX_CHILDREN = 12;

/** Richard's mockup content, used verbatim for Example mode so the example matches what he designed. */
const EXAMPLE_EMERGENCY = [
  { icon: '📞', label: 'Call first', name: 'Rachel Madrid (Mom)', phone: '(210) 555-0142', note: 'Call first in a family emergency' },
  { icon: '🚑', label: 'Nearest ER', name: 'Methodist Hospital', phone: '(210) 575-4000', address: '7700 Floyd Curl Dr — 8 min away' },
  { icon: '🏥', label: 'Urgent care', name: 'Baptist Urgent Care', phone: '(210) 555-0177', address: '2.1 mi away, open until 9pm' },
  { icon: '🚒', label: 'Fire (non-emergency)', name: 'SAFD Station 12', phone: '(210) 555-0133' },
  { icon: '🚓', label: 'Police (non-emergency)', name: 'SAPD Non-Emergency', phone: '(210) 555-0155' },
  { icon: '☣️', label: 'Poison control', name: 'Poison Control', phone: '1-800-222-1222', note: 'National hotline, 24/7' },
  { icon: '🩺', label: 'Pediatrician', name: 'Dr. Osei — Riverside Pediatrics', phone: '(210) 555-0198' },
  { icon: '🚨', label: 'Medical alert', name: 'Liam: fish allergy', note: 'EpiPen in the kitchen drawer', alert: true },
  { icon: '📍', label: 'Home address', name: '482 Alder Creek Ln, San Antonio, TX 78230' }
];

const EXAMPLE_ROWS = {
  'Home & WiFi': [['WiFi network', 'MadridHouse_5G'], ['WiFi password', 'SunnyDay482!'], ['Front door code', '2482 #'], ['Garage code', '7731']],
  'School': [['Absence line', '(210) 555-0110'], ['Early pickup', 'Front office, photo ID, before 2:30']],
  'Daily Routines': [['Mornings', 'Breakfast 7:00, out the door 7:45'], ['Screen time', '30 min after homework, none after dinner'], ['Homework', 'Kitchen table, 4:30, no devices out']],
  'Food & Allergies': [['Allergies', 'Liam: fish — EpiPen in the kitchen drawer', 'warn'], ['Food notes', 'One "no thank you" bite before dessert']],
  'Key Contacts': [['Call first', 'Rachel · (210) 555-0142'], ['Also can help', 'Grandma Carol · (210) 555-0099'], ['Dad — Richard', '(210) 555-0187'], ['Neighbor — Jen (next door)', '(210) 555-0223']],
  'Getting Around': [['Car seats', "Sofia's booster in both cars"], ['Who can collect them', 'Richard, Rachel, Grandma Carol, Jen'], ['Bus', 'Liam — Bus 14, Weds only']],
  'Pets': [['Biscuit (golden retriever)', 'Fed 7am & 6pm, 1 cup each'], ['Vet', 'Alamo Heights Animal Clinic']],
  'House Rules': [['House rules', 'Shoes off at the door. Friends over: ask first. iPad in the living room only.']]
};

/**
 * The example family's children. They live on the roster now rather than as three prose rows each, so the
 * example shows them there — the Kids card is among the first things the wizard fills, and an example
 * that left it in its empty state was advertising the hub as it stopped being a while ago.
 */
const EXAMPLE_CHILDREN = [
  { name: 'Liam', school: 'Riverside Elem.', grade: '4', teacher: 'Mrs. Hernandez', bedtime: '8:15 pm', color: 'coral' },
  { name: 'Sofia', school: 'Lincoln Elem.', grade: '2', teacher: 'Ms. Patel', bedtime: '7:30 pm', color: 'sky' }
];

/** Per-card meta lines, so the example reads like a hub someone actually lives in. */
const EXAMPLE_META = {
  'Home & WiFi': 'Updated by Richard · 4 days ago',
  'School': 'Updated by Rachel · 2 days ago',
  'Daily Routines': 'Updated by Rachel · 1 week ago',
  'Food & Allergies': 'Updated by Richard · 4 days ago',
  'Key Contacts': 'Updated by Richard · 2 weeks ago',
  'Getting Around': 'Updated by Richard · 3 weeks ago',
  'Pets': 'Updated by Rachel · 1 month ago',
  'House Rules': 'Updated by Richard · 1 month ago'
};

export function escapeHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function renderExampleTiles() {
  return EXAMPLE_EMERGENCY.map(function (t) {
    var html = '<div class="hub-tile' + (t.alert ? ' hub-tile--alert' : '') + '">';
    html += '<p class="hub-tile-label"><span aria-hidden="true">' + escapeHtml(t.icon) + '</span><span>' + escapeHtml(t.label) + '</span></p>';
    html += '<p class="hub-tile-value">' + escapeHtml(t.name) + '</p>';
    if (t.phone) html += '<span class="hub-tile-phone">' + escapeHtml(t.phone) + '</span>';
    if (t.address) html += '<span class="hub-tile-address">' + escapeHtml(t.address) + '</span>';
    if (t.note) html += '<p class="hub-tile-note">' + escapeHtml(t.note) + '</p>';
    return html + '</div>';
  }).join('');
}

export function renderExampleRows(rows) {
  // data-role matters: workspace.js's row-detail modal and hub search both read [data-role="row-key"] /
  // [data-role="row-val"], and without them an example row opens a blank modal and matches no search.
  return rows.map(function (pair) {
    var warn = pair[2] === 'warn' ? ' hub-row-val--warn' : '';
    return '<div class="hub-row"><span class="hub-row-key" data-role="row-key">' + escapeHtml(pair[0]) +
      '</span><span class="hub-row-val' + warn + '" data-role="row-val">' + escapeHtml(pair[1]) + '</span></div>';
  }).join('');
}

/** The Kids card's example rows. Mirrors the markup workspace.js builds, minus the per-child controls. */
export function renderExampleChildren() {
  return EXAMPLE_CHILDREN.map(function (c) {
    var meta = [c.school, 'Grade ' + c.grade, c.teacher, 'Bed ' + c.bedtime].join(' · ');
    return '<li class="hub-kid" data-kid-color="' + escapeHtml(c.color) + '">' +
      '<div class="hub-kid-main">' +
      '<span class="hub-kid-avatar" aria-hidden="true">' + escapeHtml(c.name.charAt(0)) + '</span>' +
      '<div class="hub-kid-text">' +
      '<p class="hub-kid-name" data-role="kid-name">' + escapeHtml(c.name) + '</p>' +
      '<p class="hub-kid-meta" data-role="kid-meta">' + escapeHtml(meta) + '</p>' +
      '</div></div></li>';
  }).join('');
}

function val(id) {
  var el = document.getElementById(id);
  return el ? el.value.trim() : '';
}

function initFamilyHubWizard() {
  'use strict';

  var data = window.__workspaceData;
  if (!data || !data.workspaceId) return;
  var workspaceId = data.workspaceId;

  function bind(id, handler) {
    var el = document.getElementById(id);
    if (el) el.addEventListener('click', handler);
  }

  // ── Example mode ───────────────────────────────────────────────────────────────────────────────
  // Nothing here is persisted. Entering stashes the real markup, exiting puts it back verbatim, so an
  // example view can never be mistaken for — or overwrite — the family's own hub.
  var stashed = null;

  /**
   * Turns example mode on. The swap is done in a helper so that a failure part-way through can be undone —
   * being left with fabricated emergency numbers and no banner or exit is worse than not showing an example.
   */
  function enterExample() {
    if (stashed) return;

    // A filter typed against the real hub hides most cards, and the example would inherit that — one card
    // and no emergency tiles under a banner promising a full example.
    clearHubSearch();

    stashed = { cards: [] };

    try {
      swapInExample();
    } catch (err) {
      exitExample();
      return;
    }

    document.body.classList.add('hub-example-mode');
    var banner = document.getElementById('hub-example-banner');
    if (banner) banner.hidden = false;
    syncNudge();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function swapInExample() {
    var grid = document.getElementById('hub-emergency-grid');
    if (grid) {
      stashed.grid = { html: grid.innerHTML, display: grid.style.display };
      grid.innerHTML = renderExampleTiles();
      grid.style.display = '';
    }

    var empty = document.getElementById('hub-emergency-empty');
    if (empty) {
      stashed.empty = empty.style.display;
      empty.style.display = 'none';
    }

    var disclaimer = document.getElementById('hub-emergency-disclaimer');
    if (disclaimer) {
      stashed.disclaimer = disclaimer.style.display;
      disclaimer.style.display = '';
    }

    document.querySelectorAll('.hub-card[data-category]').forEach(function (card) {
      var name = card.getAttribute('data-category');
      var rows = card.querySelector('[data-role="rows"]');
      var blank = card.querySelector('[data-role="card-blank"]');
      var meta = card.querySelector('[data-role="card-meta"]');
      // hasOwnProperty, not a bare lookup: a section named "constructor" or "toString" would otherwise
      // return an inherited function, pass the guard, and throw on .map.
      if (!rows || !Object.prototype.hasOwnProperty.call(EXAMPLE_ROWS, name)) return;

      stashed.cards.push({
        rows: rows, rowsHtml: rows.innerHTML,
        blank: blank, blankDisplay: blank ? blank.style.display : null,
        meta: meta, metaText: meta ? meta.textContent : null
      });

      rows.innerHTML = renderExampleRows(EXAMPLE_ROWS[name]);
      if (blank) blank.style.display = 'none';
      if (meta) meta.textContent = EXAMPLE_META[name] || 'Example content';
    });

    var kidsList = document.getElementById('hub-kids-list');
    var kidsEmpty = document.getElementById('hub-kids-empty');
    if (kidsList) {
      stashed.kids = {
        html: kidsList.innerHTML,
        display: kidsList.style.display,
        emptyDisplay: kidsEmpty ? kidsEmpty.style.display : null
      };
      kidsList.innerHTML = renderExampleChildren();
      kidsList.style.display = '';
      if (kidsEmpty) kidsEmpty.style.display = 'none';
    }
  }

  function exitExample() {
    if (!stashed) return;

    var grid = document.getElementById('hub-emergency-grid');
    if (grid && stashed.grid) {
      grid.innerHTML = stashed.grid.html;
      grid.style.display = stashed.grid.display;
    }

    var empty = document.getElementById('hub-emergency-empty');
    if (empty && stashed.empty !== undefined) empty.style.display = stashed.empty;

    var disclaimer = document.getElementById('hub-emergency-disclaimer');
    if (disclaimer && stashed.disclaimer !== undefined) disclaimer.style.display = stashed.disclaimer;

    stashed.cards.forEach(function (c) {
      c.rows.innerHTML = c.rowsHtml;
      if (c.blank) c.blank.style.display = c.blankDisplay;
      if (c.meta && c.metaText !== null) c.meta.textContent = c.metaText;
    });

    var kidsList = document.getElementById('hub-kids-list');
    var kidsEmpty = document.getElementById('hub-kids-empty');
    if (kidsList && stashed.kids) {
      kidsList.innerHTML = stashed.kids.html;
      kidsList.style.display = stashed.kids.display;
      if (kidsEmpty && stashed.kids.emptyDisplay !== null) kidsEmpty.style.display = stashed.kids.emptyDisplay;
    }

    stashed = null;
    document.body.classList.remove('hub-example-mode');

    // filterHub hides cards by setting .hidden; those were computed against example text, so anything still
    // filtered would hide the family's own cards until they cleared the box themselves.
    clearHubSearch();

    var banner = document.getElementById('hub-example-banner');
    var bannerHadFocus = banner && banner.contains(document.activeElement);
    if (banner) banner.hidden = true;
    if (bannerHadFocus) {
      var back = document.getElementById('hub-btn-example');
      if (back) back.focus();
    }

    syncNudge();
  }

  // ── Empty-hub nudge ────────────────────────────────────────────────────────────────────────────
  // Only for a hub with nothing at all in it. The per-card and per-tile empty states already cover
  // every partially-filled hub, and showing this on top of them would nag rather than help.
  /** Clears the hub search and un-hides everything filterHub hid. Safe to call when nothing is filtered. */
  function clearHubSearch() {
    var search = document.getElementById('hub-search-input');
    if (search && search.value) {
      search.value = '';
      search.dispatchEvent(new Event('input', { bubbles: true }));
    }
    document.querySelectorAll('.hub-card[hidden]').forEach(function (c) { c.hidden = false; });
    var emergency = document.getElementById('hub-emergency');
    if (emergency) emergency.hidden = false;
  }

  function syncNudge() {
    var nudge = document.getElementById('hub-nudge');
    if (!nudge) return;
    if (stashed) { nudge.hidden = true; return; }

    var hasRows = document.querySelector('.hub-rows .hub-row') !== null;
    // :not(.hub-tile--empty) matters: a brand-new hub renders one placeholder per unfilled kind, so
    // counting those made the nudge permanently invisible to exactly the people it is for.
    var hasTiles = document.querySelector('#hub-emergency-grid .hub-tile:not(.hub-tile--empty)') !== null;
    nudge.hidden = hasRows || hasTiles;
  }

  // workspace.js owns the grid and the section cards at runtime, so it has to be able to tell the nudge
  // that the hub is no longer empty. Without this the "New here?" prompt survives adding the first entry.
  window.familyHubSyncNudge = syncNudge;

  bind('hub-btn-example', enterExample);
  bind('hub-nudge-example', enterExample);
  bind('hub-example-exit', exitExample);

  // ── Wizard ─────────────────────────────────────────────────────────────────────────────────────
  var overlay = document.getElementById('hub-wizard-overlay');
  var step = 1;

  function setMessage(text, isError) {
    var msg = document.getElementById('hub-wizard-message');
    if (!msg) return;
    msg.hidden = !text;
    msg.textContent = text || '';
    msg.classList.toggle('error', !!isError);
  }

  function showStep(n) {
    step = n;
    document.querySelectorAll('.hub-wz-panel').forEach(function (p) {
      p.hidden = Number(p.getAttribute('data-panel')) !== n;
    });
    document.querySelectorAll('.hub-wz-step').forEach(function (s) {
      var sn = Number(s.getAttribute('data-step'));
      s.classList.toggle('is-active', sn === n);
      s.classList.toggle('is-done', sn < n);
      if (sn === n) s.setAttribute('aria-current', 'step');
      else s.removeAttribute('aria-current');
    });

    // Without this the dialog announces nothing on Next and there is no way to tell which step you are on.
    var panel = document.querySelector('.hub-wz-panel[data-panel="' + n + '"]');
    if (panel && !(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)) {
      // Inputs only: the first focusable element in the Kids panel is a Remove button.
      var first = panel.querySelector('input:not([type="hidden"]), select, textarea');
      // preventScroll, then reset: every step is a panel inside one scrolling form, so a plain focus()
      // scrolls that field into view from wherever the previous step was left and drops you part-way down
      // the new one. This used to be masked by scrolling the active pill into view, which went away when
      // the step strip stopped scrolling.
      if (first) first.focus({ preventScroll: true });
    }

    var form = document.getElementById('hub-wizard-form');
    if (form) form.scrollTop = 0;

    var back = document.getElementById('hub-wz-back');
    if (back) back.style.visibility = n === 1 ? 'hidden' : 'visible';

    var next = document.getElementById('hub-wz-next');
    if (next) next.textContent = n === TOTAL_STEPS ? 'Fill in my hub' : 'Next →';

    setMessage('');
    if (n === TOTAL_STEPS) renderReview();
  }

  function collect() {
    var children = [];
    if (kidsHost) {
      Array.prototype.forEach.call(kidsHost.querySelectorAll('[data-role="kid"]'), function (row) {
        var read = function (role) {
          var el = row.querySelector('[data-role="' + role + '"]');
          return el ? el.value.trim() : '';
        };
        var name = read('kid-name');
        // A name is the only thing that makes a child worth recording; everything else can arrive later.
        if (name) children.push({ name: name, bedtime: read('kid-bedtime'), school: read('kid-school'), teacher: read('kid-teacher') });
      });
    }

    var share = document.getElementById('hub-wz-create-share');

    return {
      familyName: val('hub-wz-family'),
      address: val('hub-wz-address'),
      callFirstName: val('hub-wz-callfirst-name'),
      callFirstPhone: val('hub-wz-callfirst-phone'),
      children: children,
      schoolAbsenceLine: val('hub-wz-absence'),
      pediatricianName: val('hub-wz-ped-name'),
      pediatricianPhone: val('hub-wz-ped-phone'),
      allergies: val('hub-wz-allergies'),
      wifiNetwork: val('hub-wz-wifi-network'),
      wifiPassword: val('hub-wz-wifi-password'),
      doorCode: val('hub-wz-door-code'),
      houseRules: val('hub-wz-house-rules'),
      morningRoutine: val('hub-wz-mornings'),
      screenTime: val('hub-wz-screen-time'),
      foodNotes: val('hub-wz-food-notes'),
      carSeats: val('hub-wz-car-seats'),
      pickupAuthorized: val('hub-wz-pickup'),
      pets: val('hub-wz-pets'),
      vet: val('hub-wz-vet'),
      extraContactName: val('hub-wz-extra-name'),
      extraContactPhone: val('hub-wz-extra-phone'),
      nearbyServices: readAcceptedNearby(),
      createCaregiverLink: !!(share && share.checked)
    };
  }

  /** Reads the lookup rows back out of the DOM so any edit the parent made is what gets saved. */
  function readAcceptedNearby() {
    var out = [];
    document.querySelectorAll('.hub-wz-lookup-row').forEach(function (row) {
      var include = row.querySelector('[data-role="include"]');
      if (!include || !include.checked) return;
      var name = row.querySelector('[data-role="name"]');
      var phone = row.querySelector('[data-role="phone"]');
      out.push({
        kind: row.getAttribute('data-kind'),
        name: name ? name.value.trim() : '',
        phone: phone ? phone.value.trim() : '',
        address: row.getAttribute('data-address') || ''
      });
    });
    return out.filter(function (s) { return s.name; });
  }

  function renderReview() {
    var s = collect();
    var items = [];
    if (s.familyName) items.push('Family name → <strong>' + escapeHtml(s.familyName) + '</strong>');
    if (s.address) items.push('Home address added to the emergency card');
    if (s.callFirstName || s.callFirstPhone) items.push('<strong>' + escapeHtml(s.callFirstName || 'Primary contact') + '</strong> set as the first call');
    if (s.nearbyServices.length) items.push(s.nearbyServices.length + ' local service' + (s.nearbyServices.length === 1 ? '' : 's') + ' added');
    if (s.pediatricianName || s.pediatricianPhone) items.push('Pediatrician added');
    if (s.allergies) items.push('Allergies noted on the emergency card and in Food &amp; Allergies');
    if (s.children.length) {
      items.push(s.children.length + (s.children.length === 1 ? ' child' : ' children') + ' added: ' +
        s.children.map(function (c) { return escapeHtml(c.name); }).join(', '));
    }
    if (s.children.some(function (c) { return c.school || c.teacher; }) || s.schoolAbsenceLine) items.push('School details added');
    if (s.wifiNetwork || s.wifiPassword || s.doorCode) items.push('Home &amp; WiFi details added');
    if (s.houseRules) items.push('House rules added');
    if (s.morningRoutine || s.screenTime) items.push('Daily routines added');
    if (s.carSeats || s.pickupAuthorized) items.push('Getting Around details added');
    if (s.pets || s.vet) items.push('Pets added');
    if (s.foodNotes) items.push('Food notes added');
    if (s.extraContactName) items.push(escapeHtml(s.extraContactName) + ' added to Key Contacts');
    items.push('Poison Control added — the same national number for every family');
    if (s.createCaregiverLink) items.push('A caregiver link and PIN you can send a sitter');

    var box = document.getElementById('hub-wz-review');
    if (box) box.innerHTML = '<ul class="hub-wz-review-list">' + items.map(function (i) { return '<li>' + i + '</li>'; }).join('') + '</ul>';
  }

  function openWizard() {
    if (!overlay || overlay.open) return;
    exitExample();
    var results = document.getElementById('hub-wz-lookup-results');
    if (results) results.innerHTML = '';
    overlay.showModal();
    document.body.classList.add('ws-body-locked');
    showStep(1);
  }

  function closeWizard() {
    if (!overlay || !overlay.open) return;
    overlay.close();
    document.body.classList.remove('ws-body-locked');
  }

  bind('hub-btn-wizard', openWizard);
  bind('hub-nudge-wizard', openWizard);
  bind('hub-wizard-close', closeWizard);
  bind('hub-wz-skip', closeWizard);
  bind('hub-example-try', function () { exitExample(); openWizard(); });
  bind('hub-wz-back', function () { if (step > 1) showStep(step - 1); });

  if (overlay) {
    overlay.addEventListener('cancel', function () { document.body.classList.remove('ws-body-locked'); });
  }

  // ── Kids: as many as the family has ────────────────────────────────────────────────────────────
  // The old form hardcoded two, which quietly lost the third child of a three-child family. The request
  // model was always a list; only the markup capped it.
  var kidsHost = document.getElementById('hub-wz-kids');
  var kidTemplate = document.getElementById('hub-wz-kid-template');

  function renumberKids() {
    var rows = kidsHost ? kidsHost.querySelectorAll('[data-role="kid"]') : [];
    Array.prototype.forEach.call(rows, function (row, i) {
      var label = row.querySelector('[data-role="kid-label"]');
      if (label) label.textContent = 'Child ' + (i + 1);
      var remove = row.querySelector('[data-role="kid-remove"]');
      // One row always stays: an empty Kids step should still look like somewhere to type.
      if (remove) remove.hidden = rows.length <= 1;
    });
  }

  function addKid(focus) {
    if (!kidsHost || !kidTemplate) return;
    if (kidsHost.querySelectorAll('[data-role="kid"]').length >= MAX_CHILDREN) return;

    var row = kidTemplate.content.firstElementChild.cloneNode(true);
    kidsHost.appendChild(row);
    renumberKids();
    if (focus) {
      var name = row.querySelector('[data-role="kid-name"]');
      if (name) name.focus();
    }
    syncAddKidButton();
  }

  function syncAddKidButton() {
    var btn = document.getElementById('hub-wz-add-kid');
    if (!btn || !kidsHost) return;
    btn.disabled = kidsHost.querySelectorAll('[data-role="kid"]').length >= MAX_CHILDREN;
  }

  if (kidsHost) {
    kidsHost.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-role="kid-remove"]');
      if (!remove) return;
      var row = remove.closest('[data-role="kid"]');
      if (row) row.remove();
      if (!kidsHost.querySelectorAll('[data-role="kid"]').length) addKid(false);
      renumberKids();
      syncAddKidButton();
    });
  }

  bind('hub-wz-add-kid', function () { addKid(true); });
  addKid(false);

  // ── Nearby-services lookup ─────────────────────────────────────────────────────────────────────
  var lookupBtn = document.getElementById('hub-wz-lookup');
  if (lookupBtn) {
    lookupBtn.addEventListener('click', function () {
      var results = document.getElementById('hub-wz-lookup-results');
      var address = val('hub-wz-address');
      if (!address) {
        if (results) results.innerHTML = '<p class="hub-wz-lookup-note">Add your home address in step 1 first.</p>';
        return;
      }

      lookupBtn.disabled = true;
      var original = lookupBtn.innerHTML;
      lookupBtn.textContent = 'Looking up local services…';
      if (results) results.innerHTML = '';

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/nearby-lookup', {
        method: 'POST',
        headers: csrfHeaders({ Accept: 'application/json' }),
        body: JSON.stringify({ address: address })
      })
        .then(function (r) { return r.json().catch(function () { return null; }); })
        .then(function (body) { renderLookup(body, results); })
        .catch(function () {
          if (results) results.innerHTML = '<p class="hub-wz-lookup-note">We couldn\'t run that just now — you can still add these yourself.</p>';
        })
        .finally(function () {
          lookupBtn.disabled = false;
          lookupBtn.innerHTML = original;
        });
    });
  }

  function renderLookup(body, results) {
    if (!results) return;

    if (!body || !body.succeeded) {
      var message = (body && body.error) || 'We couldn\'t find local listings — you can still add these yourself.';
      results.innerHTML = '<p class="hub-wz-lookup-note">' + escapeHtml(message) + '</p>';
      return;
    }

    var html = '<p class="hub-wz-lookup-note">Found near <strong>' + escapeHtml(body.placeLabel || 'your address') +
      '</strong>. Check each one before you rely on it — these come from public listings and can be out of date.</p>';

    html += (body.suggestions || []).map(function (s, i) {
      return '<div class="hub-wz-lookup-row" data-kind="' + escapeHtml(s.kind) + '" data-address="' + escapeHtml(s.address || '') + '">' +
        '<label class="hub-wz-lookup-include"><input type="checkbox" data-role="include" checked /> Add</label>' +
        '<input type="text" class="form-control form-control-sm" data-role="name" value="' + escapeHtml(s.name) + '" aria-label="Name" />' +
        '<input type="text" class="form-control form-control-sm" data-role="phone" value="' + escapeHtml(s.phone || '') + '" aria-label="Phone" placeholder="Phone" />' +
        '<a class="hub-wz-lookup-source" href="' + escapeHtml(s.sourceUrl) + '" target="_blank" rel="noopener noreferrer nofollow">Source</a>' +
        '</div>';
    }).join('');

    results.innerHTML = html;
  }

  // ── Submit ─────────────────────────────────────────────────────────────────────────────────────
  var form = document.getElementById('hub-wizard-form');
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (step < TOTAL_STEPS) { showStep(step + 1); return; }

      var next = document.getElementById('hub-wz-next');
      if (next) next.disabled = true;
      setMessage('');

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/setup', {
        method: 'POST',
        headers: csrfHeaders({ Accept: 'application/json' }),
        body: JSON.stringify(collect())
      })
        .then(function (r) {
          return r.json().catch(function () { return null; }).then(function (body) {
            if (!r.ok) {
              var e = new Error('setup failed');
              e.serverMessage = body && body.error;
              throw e;
            }
            return body;
          });
        })
        .then(function (result) {
          // Handed across the reload below. A toast raised here would be destroyed before it painted.
          try {
            sessionStorage.setItem('familyhq.hubSetup', JSON.stringify({
              touched: (result && result.touchedCategories) || [],
              skipped: (result && result.skipped) || [],
              share: (result && result.share) || null
            }));
          } catch (e) { /* private mode — the hub still reloads correctly, just without the flash */ }

          closeWizard();
          // Reload rather than patching the DOM: the server already renders every card, tile and
          // section-summary line, and re-deriving all of that here would be a second, drifting renderer.
          window.location.reload();
        })
        .catch(function (err) {
          setMessage(err && err.serverMessage ? err.serverMessage : 'We could not save that just now. Please try again.', true);
          if (next) next.disabled = false;
        });
    });
  }

  // ── Finish moment ──────────────────────────────────────────────────────────────────────────────
  // Runs on the load that FOLLOWS a successful wizard submit.
  (function announceSetup() {
    var raw = null;
    try {
      raw = sessionStorage.getItem('familyhq.hubSetup');
      if (raw) sessionStorage.removeItem('familyhq.hubSetup');
    } catch (e) { return; }
    if (!raw) return;

    var result;
    try { result = JSON.parse(raw); } catch (e) { return; }

    (result.touched || []).forEach(function (name) {
      var card = Array.prototype.find.call(
        document.querySelectorAll('.hub-card[data-category]'),
        function (c) { return c.getAttribute('data-category') === name; });
      if (!card) return;
      card.classList.add('hub-just-updated');
      setTimeout(function () { card.classList.remove('hub-just-updated'); }, 2400);
    });

    var emergency = document.getElementById('hub-emergency');
    if (emergency) {
      emergency.classList.add('hub-just-updated');
      setTimeout(function () { emergency.classList.remove('hub-just-updated'); }, 2400);
    }

    if (result.share && result.share.url) {
      // The whole point of the hub is having something to hand a sitter. Say so, and say the PIN, because
      // the parent has to pass that on by voice — it is deliberately not in the link.
      var pin = result.share.pin ? ' PIN ' + result.share.pin + '.' : '';
      var lead = result.share.alreadyExisted ? 'Your existing caregiver link still works.' : 'Caregiver link ready.';
      var link = location.origin + result.share.url;
      toast.success(lead + pin + ' ' + link, { duration: 12000 });
    }

    var skipped = result.skipped || [];
    if (skipped.length) {
      // Say what did not make it. Reporting a clean success over a partial one is how a parent ends up
      // trusting a hub that is missing the thing they typed.
      toast.error('Saved, except: ' + skipped.join(' ') + ' You can add those by hand.', { duration: 9000 });
    } else {
      toast.success('Your hub is filled in — you can edit anything anytime.');
    }
  })();

  syncNudge();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFamilyHubWizard);
} else {
  initFamilyHubWizard();
}
